/* ============================================================
   pc-storybook.js — les ornements « faits main »
   Port du theme-storybook.js de ploumi.fr, réécrit et commenté.
   Zéro dépendance.

   1. Bords festonnés  — calcul d'un chemin SVG ondulé autour de
      chaque image, appliqué en clip-path + trait pointillé.
   2. Barre de progression du scroll avec goutte de sérum.
   3. Rails horizontaux : barre proportionnelle + flèches.
   4. Hero : flèches injectées, textes synchronisés par slide.
   5. FAQ : numéros 01..NN injectés.
   6. Halo signature : déclenchement au scroll.
   7. Ruban d'annonce : duplication pour boucle infinie.
   8. Retour en haut.
   9. Transition de page en pétales.
   ============================================================ */
(function () {
  'use strict';

  var q  = function (s, r) { return (r || document).querySelector(s); };
  var qa = function (s, r) { return Array.prototype.slice.call((r || document).querySelectorAll(s)); };
  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var ICON = {
    prev: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M15 5l-7 7 7 7"/></svg>',
    next: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M9 5l7 7-7 7"/></svg>',
    up:   '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M5 15l7-7 7 7"/></svg>'
  };

  /* ==========================================================
     1. BORDS FESTONNÉS
     ----------------------------------------------------------
     Pour chaque conteneur ciblé, on mesure l'image, on génère
     un contour en courbes de Bézier alternées (haut et bas
     ondulés, côtés pincés), puis :
       - clip-path: path(...)  découpe l'image
       - un <svg> superposé retrace le même chemin en pointillé
     Recalculé par ResizeObserver + MutationObserver.
     ========================================================== */

  /* Sélecteurs à festonner, avec l'index de variante de départ */
  var EMB_TARGETS = [
    /* `.card-media` et non `#collection .card-media` : la même carte
       est rendue par pc-collection (accueil), pc-collection-main
       (page de collection) et pc-simple-main (recherche). Cadrée sur
       l'ancre #collection, la broderie ne se dessinait que sur
       l'accueil, et disparaissait dès que l'ancre était renommée. */
    ['.card-media',                    0],
    ['.bundle-imgs .pc-emb-wrap',      1],
    ['.pc-edito-media',                2],
    ['.mq-photo',                      1],
    ['.univers-grid figure',           0],
    ['.pdp .gallery-main',             0],
    ['.steps .step-img',               1],
    ['.pdp .bb-ugc-grid a',            2],
    ['.obj-media',                     0]
  ];

  /* Trois rythmes d'ondulation : nombre de vagues en haut / en bas, amplitude */
  var VARIANTS = [
    { nTop: 3, nBot: 3, amp: 0.42 },
    { nTop: 2, nBot: 3, amp: 0.55 },
    { nTop: 3, nBot: 2, amp: 0.34 }
  ];

  var resizeObs = null;

  function r2(n) { return Math.round(n * 100) / 100; }

  /* Suite de courbes ondulées de (x1,y1) à (x2,y2) */
  function waveRun(x1, y1, x2, y2, nx, ny, count, amp, dir) {
    var out = '';
    var dx = (x2 - x1) / count, dy = (y2 - y1) / count;
    for (var i = 0; i < count; i++) {
      var ax = x1 + dx * i,  ay = y1 + dy * i;
      var bx = ax + dx,      by = ay + dy;
      var ox = nx * amp * dir, oy = ny * amp * dir;
      out += 'C' + r2(ax + dx * 0.3 + ox) + ' ' + r2(ay + dy * 0.3 + oy) +
             ' ' + r2(bx - dx * 0.3 + ox) + ' ' + r2(by - dy * 0.3 + oy) +
             ' ' + r2(bx) + ' ' + r2(by);
      dir = -dir;
    }
    return out;
  }

  /* Contour complet pour une boîte w×h */
  function buildPath(w, h, v) {
    var inset = Math.max(7, Math.min(16, Math.min(w, h) * 0.075));
    var L = inset, T = inset, R = w - inset, B = h - inset;
    if (R - L < 46 || B - T < 46) return '';

    var corner = Math.min(20, (R - L) / 3.2, (B - T) / 3.2);
    var pinch  = Math.min(inset, (B - T) / 2 - corner - 3);
    if (pinch < 5) pinch = 0;

    var amp = inset * v.amp;
    var midY = (T + B) / 2;
    var d = 'M' + r2(L + corner) + ' ' + r2(T);

    d += waveRun(L + corner, T, R - corner, T, 0, -1, v.nTop, amp, 1);
    d += 'Q' + r2(R) + ' ' + r2(T) + ' ' + r2(R) + ' ' + r2(T + corner);
    if (pinch) d += 'V' + r2(midY - pinch) + 'A' + r2(pinch) + ' ' + r2(pinch) + ' 0 0 1 ' + r2(R) + ' ' + r2(midY + pinch);
    d += 'V' + r2(B - corner);
    d += 'Q' + r2(R) + ' ' + r2(B) + ' ' + r2(R - corner) + ' ' + r2(B);
    d += waveRun(R - corner, B, L + corner, B, 0, 1, v.nBot, amp, -1);
    d += 'Q' + r2(L) + ' ' + r2(B) + ' ' + r2(L) + ' ' + r2(B - corner);
    if (pinch) d += 'V' + r2(midY + pinch) + 'A' + r2(pinch) + ' ' + r2(pinch) + ' 0 0 0 ' + r2(L) + ' ' + r2(midY - pinch);
    d += 'V' + r2(T + corner);
    d += 'Q' + r2(L) + ' ' + r2(T) + ' ' + r2(L + corner) + ' ' + r2(T) + 'Z';
    return d;
  }

  /* offsetWidth / offsetLeft n'existent que sur HTMLElement.
     Un <svg> inline renvoie undefined — d'où un repli sur les
     rectangles, sinon la broderie ne se dessine jamais sur les
     médias vectoriels. */
  function measure(box) {
    var img = box.pcImg;
    var w = img.offsetWidth, h = img.offsetHeight;
    var l = img.offsetLeft,  t = img.offsetTop;
    if (!w || !h) {
      var ri = img.getBoundingClientRect();
      var rb = box.getBoundingClientRect();
      w = Math.round(ri.width);  h = Math.round(ri.height);
      l = Math.round(ri.left - rb.left);
      t = Math.round(ri.top  - rb.top);
    }
    return [w, h, l, t];
  }

  function paint(box, m) {
    var img = box.pcImg, svg = box.pcSvg;
    var w = m[0], h = m[1];
    if (!w || !h || w <= 0 || h <= 0) return;     /* pas encore mis en page */
    if (w === box.pcW && h === box.pcH) return;   /* rien n'a bougé */
    box.pcW = w; box.pcH = h;

    var d = buildPath(w, h, VARIANTS[box.pcVar]);
    if (!d) { img.style.clipPath = ''; svg.style.display = 'none'; return; }

    svg.style.display = '';
    img.style.clipPath = 'path("' + d + '")';
    svg.setAttribute('viewBox', '0 0 ' + w + ' ' + h);
    svg.style.left   = m[2] + 'px';
    svg.style.top    = m[3] + 'px';
    svg.style.width  = w + 'px';
    svg.style.height = h + 'px';
    svg.firstChild.setAttribute('d', d);
  }

  /* On mesure TOUT avant de peindre : évite le layout thrashing */
  function repaint(boxes) {
    var m = [], i;
    for (i = 0; i < boxes.length; i++) m.push(measure(boxes[i]));
    for (i = 0; i < boxes.length; i++) paint(boxes[i], m[i]);
  }

  function attach(box, img, variant, needsRelative) {
    if (needsRelative) box.style.position = 'relative';
    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', 'pc-emb-line');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    svg.setAttribute('preserveAspectRatio', 'none');
    svg.appendChild(document.createElementNS('http://www.w3.org/2000/svg', 'path'));
    box.appendChild(svg);
    box.pcImg = img; box.pcSvg = svg; box.pcVar = variant;
    img.pcBox = box;
    return box;
  }

  function collect() {
    var pending = [];
    EMB_TARGETS.forEach(function (t) {
      qa(t[0]).forEach(function (box, i) {
        if (box.pcImg) return;
        var img = box.querySelector('img, svg.pc-illus, .pc-fill');
        if (!img) return;
        box.classList.add('pc-emb');
        pending.push([box, img, (t[1] + i) % VARIANTS.length,
                      getComputedStyle(box).position === 'static']);
      });
    });
    return pending.map(function (p) { return attach(p[0], p[1], p[2], p[3]); });
  }

  function embroider() {
    if (!resizeObs && window.ResizeObserver) {
      resizeObs = new ResizeObserver(function (entries) {
        var boxes = [];
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].target.pcBox) boxes.push(entries[i].target.pcBox);
        }
        if (boxes.length) repaint(boxes);
      });
    }
    var fresh = collect();
    if (!fresh.length) return;
    repaint(fresh);
    if (resizeObs) fresh.forEach(function (b) { resizeObs.observe(b.pcImg); });
  }

  /* ==========================================================
     2. BARRE DE PROGRESSION + GOUTTE
     ========================================================== */
  var lastHeadH = -1, lastHeadLeft = -1;

  /* Lire d'abord, écrire ensuite — jamais l'inverse.
     `--pc-head-h` est posée sur :root. Une custom property est héritée :
     l'écrire invalide le style calculé de TOUT le document. Tant qu'on
     n'observe rien, le navigateur diffère le recalcul — mais la moindre
     lecture de layout qui suit l'oblige à tout rattraper, arbre entier
     compris. L'ancienne version alternait lecture, écriture, lecture :
     9,46 ms par frame mesurées sur une page de 1 951 nœuds, contre
     0,12 ms pour les mêmes lectures une fois les deux phases séparées.
     Le coût suivait la taille du DOM (+57 % de nœuds, +73 % de coût),
     donc il était pire sur Shopify — où chaque section ajoute son
     wrapper .shopify-section — que dans preview/. */
  function readHeaderVars() {
    var h = q('.site-header');
    if (!h) return null;
    var t = q('.menu-toggle');
    return {
      bottom: Math.round(h.getBoundingClientRect().bottom),
      left: t ? Math.round(t.getBoundingClientRect().left) : null
    };
  }

  function writeHeaderVars(m) {
    if (!m) return;
    var s = document.documentElement.style;
    if (m.bottom !== lastHeadH) {
      lastHeadH = m.bottom;
      s.setProperty('--pc-head-h', m.bottom + 'px');
    }
    if (m.left !== null && m.left !== lastHeadLeft) {
      lastHeadLeft = m.left;
      s.setProperty('--pc-head-left', m.left + 'px');
    }
  }

  /* Passe complète, pour les appels hors scroll (load, resize). */
  function syncHeaderVars() { writeHeaderVars(readHeaderVars()); }

  function initProgress() {
    var head = q('.site-header');
    if (!head || q('.pc-progress')) return;

    var bar = document.createElement('div');
    bar.className = 'pc-progress';
    bar.setAttribute('aria-hidden', 'true');
    bar.innerHTML = '<i></i><b></b><u></u>';

    var pct = function () {
      var span = document.documentElement.scrollHeight - window.innerHeight;
      return span > 0 ? Math.min(100, Math.max(0, window.scrollY / span * 100)) : 0;
    };

    bar.style.setProperty('--pc-p', pct().toFixed(2) + '%');
    head.appendChild(bar);

    /* Un seul passage par frame, et les deux lectures groupées devant
       les deux écritures : le reflow forcé disparaît. */
    var queued = false;
    var frame = function () {
      queued = false;
      var p = pct();                                        /* lecture  */
      var m = readHeaderVars();                             /* lecture  */
      bar.style.setProperty('--pc-p', p.toFixed(2) + '%');  /* écriture */
      writeHeaderVars(m);                                   /* écriture */
    };
    var tick = function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(frame);
    };
    window.addEventListener('scroll', tick, { passive: true });
    window.addEventListener('resize', tick);
    frame();

    var ready = function () { setTimeout(function () { bar.classList.add('is-ready'); }, 150); };
    if (document.readyState === 'complete') ready();
    else window.addEventListener('load', ready, { once: true });
  }

  /* ==========================================================
     3. RAILS HORIZONTAUX
     ========================================================== */
  function initRails() {
    qa('[data-rail]').forEach(function (rail) {
      if (rail.pcRail) return;
      var track = q('.pc-rail-track', rail);
      if (!track) return;
      rail.pcRail = 1;

      var foot = document.createElement('div');
      foot.className = 'pc-rail-foot';
      foot.innerHTML =
        '<div class="pc-rail-bar" aria-hidden="true"><u></u></div>' +
        '<div class="pc-rail-nav">' +
          '<button type="button" aria-label="Précédent">' + ICON.prev + '</button>' +
          '<button type="button" aria-label="Suivant">'   + ICON.next + '</button>' +
        '</div>';
      rail.appendChild(foot);

      var bar  = q('.pc-rail-bar', foot);
      var btns = qa('button', foot);

      var step = function () {
        var first = track.firstElementChild;
        return first ? first.getBoundingClientRect().width + 20 : track.clientWidth * 0.8;
      };
      btns[0].addEventListener('click', function () {
        track.scrollBy({ left: -step(), behavior: REDUCED ? 'auto' : 'smooth' });
      });
      btns[1].addEventListener('click', function () {
        track.scrollBy({ left: step(), behavior: REDUCED ? 'auto' : 'smooth' });
      });

      var update = function () {
        var span    = track.scrollWidth - track.clientWidth;
        var visible = track.clientWidth / Math.max(track.scrollWidth, 1);
        var ratio   = span > 4 ? track.scrollLeft / span : 0;
        var minW    = Math.max(visible * 100, 10);
        bar.style.setProperty('--pc-rp', (minW + ratio * (100 - minW)).toFixed(2) + '%');
        btns[0].disabled = ratio <= 0.01;
        btns[1].disabled = ratio >= 0.99;
        foot.style.display = span > 4 ? '' : 'none';
      };
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();

      if (rail.hasAttribute('data-autoplay')) autoplayRail(rail, track, btns, step);
    });
  }

  /* Défilement continu et lent d'un rail [data-autoplay], en boucle.
     Les cartes sont doublées : arrivé au début du double, on recule
     d'une longueur de boucle, sans que l'œil voie le saut. Pause au
     survol, au toucher, au clavier et hors écran ; rien si le visiteur
     a demandé moins d'animations. */
  function autoplayRail(rail, track, btns, step) {
    if (REDUCED || rail.pcAuto) return;
    rail.pcAuto = 1;
    var items = Array.prototype.slice.call(track.children);
    if (items.length < 2) return;
    items.forEach(function (el) {
      var c = el.cloneNode(true);
      c.setAttribute('aria-hidden', 'true');
      c.removeAttribute('data-reveal');
      c.classList.add('is-clone');
      qa('a,button', c).forEach(function (a) { a.tabIndex = -1; });
      track.appendChild(c);
    });
    var clone0 = track.children[items.length];
    var loopW = function () { return clone0.offsetLeft - items[0].offsetLeft; };

    var SPEED = parseFloat(rail.getAttribute('data-autoplay')) || 22; // px par seconde
    var pos = track.scrollLeft, last = 0, held = false, seen = true, wait = 0;

    var wrap = function () {
      var w = loopW();
      if (w > 0 && track.scrollLeft >= w) { pos = track.scrollLeft - w; track.scrollLeft = pos; }
    };
    var tick = function (t) {
      var dt = last ? Math.min(t - last, 64) : 0;
      last = t;
      if (!held && seen && !document.hidden) {
        pos += SPEED * dt / 1000;
        var w = loopW();
        if (w > 0 && pos >= w) pos -= w;
        track.scrollLeft = pos;
      }
      requestAnimationFrame(tick);
    };
    var hold = function () {
      held = true; clearTimeout(wait);
      rail.classList.remove('is-playing');
    };
    var release = function (ms) {
      clearTimeout(wait);
      wait = setTimeout(function () {
        wrap(); pos = track.scrollLeft; last = 0; held = false;
        rail.classList.add('is-playing');
      }, ms);
    };

    rail.addEventListener('mouseenter', hold);
    rail.addEventListener('mouseleave', function () { release(400); });
    track.addEventListener('touchstart', hold, { passive: true });
    track.addEventListener('touchend', function () { release(3000); }, { passive: true });
    track.addEventListener('wheel', function () { hold(); release(3000); }, { passive: true });
    rail.addEventListener('focusin', hold);
    rail.addEventListener('focusout', function () { release(1500); });

    // Flèches : un cran, puis reprise. En arrière depuis le début, on
    // saute d'abord d'une boucle pour avoir de la marge.
    btns[0].addEventListener('click', function () {
      if (track.scrollLeft < step()) { track.scrollLeft += loopW(); }
      hold(); release(3500);
    }, true);
    btns[1].addEventListener('click', function () {
      wrap(); hold(); release(3500);
    }, true);
    // La boucle n'a ni début ni fin : les deux flèches restent actives.
    track.addEventListener('scroll', function () {
      btns[0].disabled = btns[1].disabled = false;
    }, { passive: true });
    btns[0].disabled = btns[1].disabled = false;

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (e) { seen = e[0].isIntersecting; }).observe(rail);
    }
    rail.classList.add('is-autoplay', 'is-playing');
    requestAnimationFrame(tick);
  }

  /* ==========================================================
     4. HERO
     ---------------------------------------------------------- */
  function initHero() {
    var hero = q('.hero-banner');
    if (!hero || hero.pcHero) return;
    hero.pcHero = 1;

    var dots   = qa('.hero-dots button', hero);
    var slides = qa('.hero-slide', hero);
    if (!slides.length) return;

    /* Flèches injectées */
    if (dots.length > 1) {
      ['prev', 'next'].forEach(function (dir) {
        var b = document.createElement('button');
        b.className = 'pc-hero-arrow ' + dir;
        b.type = 'button';
        b.setAttribute('aria-label', dir === 'prev' ? 'Visuel précédent' : 'Visuel suivant');
        b.innerHTML = ICON[dir];
        b.addEventListener('click', function () {
          var i = dots.findIndex(function (d) { return d.classList.contains('active'); });
          if (i < 0) i = 0;
          dots[(i + (dir === 'next' ? 1 : -1) + dots.length) % dots.length].click();
        });
        /* Dans la rangée de navigation quand elle existe : flèche,
           puces, flèche — sinon sur les bords de la scène. */
        var nav = q('.hero-nav', hero);
        if (nav) nav[dir === 'prev' ? 'insertBefore' : 'appendChild'](b, nav.firstChild);
        else (q('.hero-stage', hero) || hero).appendChild(b);
      });
    }

    /* Libellé du bouton : s'efface lettre par lettre puis se réécrit
       avec celui du visuel suivant. Un nouveau changement de visuel
       annule la frappe en cours. */
    var cta = q('.hero-cta', hero);
    var ctaTxt = cta && q('.hero-cta-txt', cta);
    var typing = null;
    var target = ctaTxt ? ctaTxt.textContent : '';
    var typeCta = function (label, url) {
      if (!ctaTxt || !label) return;
      if (url) cta.setAttribute('href', url);
      cta.setAttribute('aria-label', label);
      /* L'observateur appelle sync plusieurs fois par changement
         (slides et puces) : on ne relance que si la cible change. */
      if (label === target) return;
      target = label;
      clearTimeout(typing);
      if (REDUCED) { ctaTxt.textContent = label; return; }
      cta.classList.add('is-typing');
      var erase = function () {
        var cur = ctaTxt.textContent;
        if (cur.length) { ctaTxt.textContent = cur.slice(0, -1); typing = setTimeout(erase, 26); }
        else typing = setTimeout(write, 160);
      };
      var n = 0;
      var write = function () {
        n++;
        ctaTxt.textContent = label.slice(0, n);
        if (n < label.length) typing = setTimeout(write, 52);
        else { typing = null; cta.classList.remove('is-typing'); }
      };
      erase();
    };

    /* Le texte suit la slide active.
       `data-hero-sync="off"` sur la scène fige le texte du premier
       visuel : seule la photo change. Le réglage existe parce que
       remplacer titre, sur-titre et accroche d'un coup fait sauter la
       hauteur du bloc à chaque rotation — acceptable quand le texte
       raconte la slide, gênant quand il porte la promesse de marque.
       Le style par slide (`data-pc-style`) continue de suivre. */
    var stage = q('.hero-stage', hero) || hero;
    var syncText = stage.getAttribute('data-hero-sync') !== 'off';
    var h1 = q('.hero-copy h1', hero);
    var ld = q('.hero-copy .lead', hero);
    var eb = q('.hero-copy .eyebrow', hero);
    if (!h1) return;

    var sync = function () {
      var active = slides.filter(function (s) { return s.classList.contains('active'); })[0];
      if (!active) return;
      hero.setAttribute('data-pc-style', active.getAttribute('data-style') || 'a');
      if (!syncText) return;
      var t = active.getAttribute('data-hero-title');
      var l = active.getAttribute('data-hero-lead');
      var e = active.getAttribute('data-hero-eyebrow');
      if (t && h1.innerHTML !== t) h1.innerHTML = t;
      if (l && ld) ld.textContent = l;
      if (e && eb) eb.textContent = e;
      typeCta(active.getAttribute('data-hero-cta'), active.getAttribute('data-hero-cta-url'));
    };
    new MutationObserver(sync).observe(hero, {
      subtree: true, attributes: true, attributeFilter: ['class']
    });
    sync();
  }

  /* ==========================================================
     5. FAQ NUMÉROTÉE
     ========================================================== */
  function numberFaq() {
    qa('.faq-numbered').forEach(function (wrap) {
      if (wrap.classList.contains('pc-faq-done')) return;
      var accs = qa('.acc', wrap);
      if (!accs.length) return;
      accs.forEach(function (acc, i) {
        var n = document.createElement('span');
        n.className = 'pc-faq-n';
        n.setAttribute('aria-hidden', 'true');
        n.textContent = (i + 1 < 10 ? '0' : '') + (i + 1);
        acc.insertBefore(n, acc.firstChild);
      });
      wrap.classList.add('pc-faq-done');
    });
  }

  /* ==========================================================
     6. HALO SIGNATURE
     ========================================================== */
  function initHalo() {
    qa('.pc-halo').forEach(function (el) {
      if (el.dataset.pcHalo) return;
      el.dataset.pcHalo = '1';
      if (!window.IntersectionObserver) { el.classList.add('on'); return; }
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { e.target.classList.add('on'); io.unobserve(e.target); }
        });
      }, { rootMargin: '0px 0px -12% 0px' });
      io.observe(el);
    });
  }

  /* ==========================================================
     7. RUBAN D'ANNONCE — duplication pour boucle sans couture
     ========================================================== */
  function initRibbon() {
    var rot = q('.announce-rotator');
    if (!rot || rot.dataset.pcRibbon) return;
    var html = rot.innerHTML;
    if (!html.trim()) return;
    rot.dataset.pcRibbon = '1';
    var unit   = rot.scrollWidth || 1;
    var copies = Math.max(1, Math.ceil((window.innerWidth + 240) / unit));
    var block  = new Array(copies).fill(html).join('');
    rot.innerHTML = block + block;   /* x2 : la boucle translate de -50% */
  }

  /* ==========================================================
     8. RETOUR EN HAUT
     ========================================================== */
  function initBackTop() {
    if (q('.pc-top')) return;
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'pc-top';
    b.setAttribute('aria-label', 'Revenir en haut');
    b.innerHTML = ICON.up;
    b.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: REDUCED ? 'auto' : 'smooth' });
    });
    document.body.appendChild(b);
    /* `scrollY` ne coûte rien, mais le toggle reste groupé dans la frame :
       il ne doit pas écrire pendant qu'un autre handler lit le layout. */
    var queued = false;
    var tick = function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        b.classList.toggle('show', window.scrollY > 620);
      });
    };
    window.addEventListener('scroll', tick, { passive: true });
    b.classList.toggle('show', window.scrollY > 620);
  }

  /* ==========================================================
     9. TRANSITION DE PAGE EN PÉTALES
     ---------------------------------------------------------- */
  function buildPetalGrid() {
    var sky = q('.pt-sky');
    if (!sky || sky.dataset.pcBuilt) return;
    sky.dataset.pcBuilt = '1';
    var cols = Math.ceil(window.innerWidth / 168) + 1;
    var rows = Math.ceil(window.innerHeight / 104) + 1;
    var frag = document.createDocumentFragment();
    for (var r = 0; r < rows; r++) {
      for (var c = 0; c < cols; c++) {
        var i = document.createElement('i');
        i.style.left = (c * 168) + 'px';
        i.style.top  = (r * 104) + 'px';
        i.style.width  = '168px';
        i.style.height = '104px';
        i.style.setProperty('--c', c);
        i.style.setProperty('--d', Math.min(3, r));
        frag.appendChild(i);
      }
    }
    sky.appendChild(frag);
  }

  /* L'ouverture est faite par le CSS. Ici, uniquement le
     garde-fou : si quelque chose a laissé `pt-cover` posé
     (retour arrière depuis le cache, navigation annulée),
     on le retire. */
  function releaseCurtain() {
    document.body.classList.remove('pt-cover');
    var pt = q('.pt');
    /* On ne masque pas si une navigation sortante vient de réarmer
       le rideau : `pt-cover` est alors posé sur le body. */
    if (pt && !document.body.classList.contains('pt-cover')) pt.classList.add('pt-done');
  }
  window.addEventListener('pageshow', releaseCurtain);
  /* Deux filets : la fin normale de l'animation, puis un délai dur. */
  window.addEventListener('load', function () { setTimeout(releaseCurtain, 1400); });
  setTimeout(releaseCurtain, 2500);

  /* Rideau au clic sur un lien interne */
  function initCurtainLinks() {
    if (REDUCED) return;
    document.addEventListener('click', function (e) {
      var a = e.target.closest ? e.target.closest('a[href]') : null;
      if (!a) return;
      var href = a.getAttribute('href');
      if (!href || href.charAt(0) === '#' || a.target === '_blank') return;
      if (a.hostname && a.hostname !== location.hostname) return;
      if (/^(mailto:|tel:|javascript:)/i.test(href)) return;
      if (a.hasAttribute('download') || a.dataset.noCurtain !== undefined) return;
      var pt = q('.pt');
      if (!pt) return;
      e.preventDefault();
      pt.classList.remove('pt-done');
      document.body.classList.add('pt-cover');
      setTimeout(function () { location.href = a.href; }, 520);
    });
  }

  /* ==========================================================
     BOOT
     ========================================================== */
  function nextFrame(fn) {
    if (!window.requestAnimationFrame) { fn(); return; }
    requestAnimationFrame(function () { setTimeout(fn, 0); });
  }

  function boot() {
    buildPetalGrid();
    initRibbon();
    syncHeaderVars();
    initProgress();
    initHero();
    initRails();
    initBackTop();
    numberFaq();
    initHalo();
    initCurtainLinks();

    releaseCurtain();
    if (REDUCED) embroider();
    else nextFrame(embroider);

    window.addEventListener('resize', syncHeaderVars);
    window.addEventListener('load', function () { syncHeaderVars(); numberFaq(); embroider(); });

    /* Les images arrivent tard (lazy, polices, CDN) : on repasse */
    setTimeout(function () { numberFaq(); embroider(); }, 1200);
    setTimeout(embroider, 3000);

    if (window.MutationObserver) {
      var t = 0;
      new MutationObserver(function () {
        clearTimeout(t);
        t = setTimeout(embroider, 260);
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
